import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'

const APPOINTMENTS = [
  {
    time: '11:00',
    end: '12:15',
    guest: 'Fatima Al-Rashid',
    service: 'Royal Hydra Facial',
    therapist: 'Aisha',
    duration: '75 min',
    status: 'in_treatment',
    vip: true,
    countdown: 'In treatment · 32 min left',
  },
  {
    time: '11:30',
    end: '12:30',
    guest: 'Layla Mansour',
    service: 'Hot Stone Massage',
    therapist: 'Lina',
    duration: '60 min',
    status: 'in_treatment',
    vip: false,
    countdown: 'In treatment · 18 min left',
  },
  {
    time: '12:00',
    end: '13:00',
    guest: 'Mariam Hassan',
    service: 'Luxury Manicure & Gel',
    therapist: 'Hana',
    duration: '60 min',
    status: 'checked_in',
    vip: false,
    countdown: 'Checked in · awaiting room',
  },
  {
    time: '13:00',
    end: '13:45',
    guest: 'Noor Al-Said',
    service: 'Bridal Glow Package',
    therapist: 'Aisha',
    duration: '45 min',
    status: 'arriving',
    vip: true,
    countdown: 'VIP arrives in 42 min',
  },
  {
    time: '13:30',
    end: '14:00',
    guest: 'Huda M.',
    service: 'Aromatherapy Massage',
    therapist: 'Lina',
    duration: '30 min',
    status: 'late',
    vip: false,
    countdown: 'Late by 8 minutes',
  },
  {
    time: '14:30',
    end: '15:30',
    guest: 'Sara Khalifa',
    service: 'Keratin Hair Spa',
    therapist: 'Lina',
    duration: '60 min',
    status: 'upcoming',
    vip: false,
    countdown: 'Starts in 2h 12m',
  },
  {
    time: '16:00',
    end: '17:30',
    guest: 'Amira Touma',
    service: 'Full Body Wax & Facial',
    therapist: 'Hana',
    duration: '90 min',
    status: 'upcoming',
    vip: false,
    countdown: 'Starts in 3h 42m',
  },
]

const STATUS_STYLES = {
  in_treatment: {
    badge: 'bg-primary text-primary-foreground px-2.5 py-0.5 text-[10px] font-bold',
    label: 'In treatment',
    dot: 'bg-primary ring-4 ring-primary/20',
    countdown: 'text-primary font-semibold',
    card: 'bg-primary/[0.04] shadow-sm',
  },
  checked_in: {
    badge: 'bg-sky-500/15 text-sky-700 px-2.5 py-0.5 text-[10px] font-bold',
    label: 'Checked in',
    dot: 'bg-sky-500 ring-4 ring-sky-500/15',
    countdown: 'text-sky-700 font-medium',
    card: 'bg-sky-50/40',
  },
  arriving: {
    badge: 'bg-gold/25 text-[hsl(31_45%_35%)] px-2.5 py-0.5 text-[10px] font-bold',
    label: 'VIP arriving',
    dot: 'bg-gold ring-4 ring-gold/25',
    countdown: 'text-[hsl(31_45%_38%)] font-semibold',
    card: 'bg-gold/[0.06]',
  },
  late: {
    badge: 'bg-red-500/12 text-red-600 px-2.5 py-0.5 text-[10px] font-bold',
    label: 'Late',
    dot: 'bg-red-500 ring-4 ring-red-500/15',
    countdown: 'text-red-600 font-semibold',
    card: 'bg-red-50/50',
  },
  upcoming: {
    badge: 'bg-muted text-muted-foreground px-2.5 py-0.5 text-[10px] font-bold',
    label: 'Upcoming',
    dot: 'bg-muted-foreground/30',
    countdown: 'text-muted-foreground',
    card: 'bg-card',
  },
  completed: {
    badge: 'bg-emerald-500/12 text-emerald-700 px-2.5 py-0.5 text-[10px] font-bold',
    label: 'Completed',
    dot: 'bg-emerald-500',
    countdown: 'text-emerald-600',
    card: 'bg-emerald-50/30',
  },
  no_show: {
    badge: 'bg-red-500/12 text-red-600 px-2.5 py-0.5 text-[10px] font-bold',
    label: 'No-show',
    dot: 'bg-red-500 ring-4 ring-red-500/15',
    countdown: 'text-red-600 font-semibold',
    card: 'bg-red-50/50',
  },
}

/** Map API timeline status to a known style key. */
function timelineStatusKey(status) {
  if (status === 'scheduled') return 'upcoming'
  return status in STATUS_STYLES ? status : 'upcoming'
}

function timelineStatusStyle(status, vip) {
  const key = timelineStatusKey(status)
  const style = STATUS_STYLES[key]
  if (key === 'arriving' && !vip) {
    return { ...style, label: 'Arriving soon' }
  }
  return style
}

export function AppointmentTimeline() {
  const { data } = useCommandDashboard()
  const timeline = data?.timeline ?? {}
  const now = timeline.server_now ?? '—'
  const appointments = Array.isArray(timeline.appointments)
    ? timeline.appointments.map((row) => ({
        ...row,
        countdown: row.countdown_label ?? row.countdown,
      }))
    : []

  return (
    <section className="card-surface p-4 lg:p-5">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Guest flow · timeline
        </h2>
        <div className="inline-flex items-center gap-2 self-start rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary shadow-sm sm:self-auto">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-40" />
            <span className="relative h-2 w-2 rounded-full bg-primary" />
          </span>
          Now {now}
        </div>
      </div>

      <div className="relative">
        <div className="timeline-rail absolute bottom-0 left-[88px] top-0 w-1 rounded-full sm:left-[100px]" />

        <div className="relative z-10 mb-4 flex items-center gap-2 pl-[68px] sm:pl-[80px]">
          <div className="absolute left-[72px] sm:left-[84px]">
            <span className="timeline-now-glow block h-5 w-5 rounded-full border-[3px] border-white bg-primary" />
          </div>
          <span className="rounded-full bg-gradient-to-r from-primary via-secondary to-gold px-3.5 py-1.5 text-[11px] font-bold text-white shadow-glow">
            ▼ Current time · {now}
          </span>
        </div>

        <div className="space-y-2">
          {appointments.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No appointments scheduled for today.</p>
          ) : null}
          {appointments.map((apt, i) => {
            const st = timelineStatusStyle(apt.status, apt.vip)
            return (
              <motion.div
                key={apt.id ?? `${apt.time}-${apt.guest}-${i}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + i * 0.04 }}
                whileHover={{ x: 2 }}
                className="relative flex gap-4 sm:gap-5"
              >
                <div className="w-[72px] shrink-0 pt-3 text-right sm:w-[84px]">
                  <p className="text-sm font-bold tabular-nums text-foreground">{apt.time}</p>
                  <p className="text-[10px] text-muted-foreground">→ {apt.end}</p>
                </div>

                <div className="relative flex shrink-0 flex-col items-center pt-4">
                  <span className={cn('z-10 h-3.5 w-3.5 rounded-full shadow-sm', st.dot)} />
                </div>

                <div
                  className={cn(
                    'mb-0.5 min-w-0 flex-1 rounded-2xl p-3 transition-all duration-200 hover:shadow-luxury-sm',
                    st.card,
                  )}
                  style={{ border: '1px solid hsl(262 20% 50% / 0.04)' }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-foreground">{apt.guest}</p>
                        {apt.vip && (
                          <span className="rounded-md bg-gradient-to-r from-gold/30 to-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[hsl(31_45%_35%)]">
                            ★ VIP
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{apt.service}</p>
                    </div>
                    <span className={cn('shrink-0 rounded-full', st.badge)}>{st.label}</span>
                  </div>

                  <p className={cn('mt-2 text-xs', st.countdown)}>{apt.countdown}</p>

                  <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    <span>
                      Therapist ·{' '}
                      <strong className="font-semibold text-foreground">{apt.therapist}</strong>
                    </span>
                    <span>
                      Duration ·{' '}
                      <strong className="font-semibold text-foreground">{apt.duration}</strong>
                    </span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
